import {
  getProductCLickSearchPayload,
  getProductPayload,
} from '@helpers/analytics/payload-analytics';
import { handleAddProductListOrigin } from '@helpers/analytics/productListOrigin';
import trackEvent from 'event-tracks';
import { parseCookies } from 'nookies';
import analytics from 'services/analytics/analytics';
import { ProductEvents, ProductInteraction } from 'types/analytics/product/product.enum';
import {
  TBuyTogetherAddToCart,
  TBuyTogetherChecked,
  TBuyTogetherClickProduct,
  TInteractionEvent,
  TListFilteredProps,
  TListViewedProps,
  TProductAdded,
  TProductAddedToWishlist,
  TProductBase,
  TProductCartInteraction,
  TProductCheckInstallments,
  TProductClicked,
  TProductExtendedWarranty,
  TProductViewed,
  TWishlistButtonClicked,
} from 'types/analytics/product/product.types';
import { PAGE_TYPE } from 'types/page/page.types';
import { IBuyBox, IProduct, TPdpV2Data, TProduct, TProductClick } from 'types/product';

export function trackProductClick(eventData: TProductClicked): Promise<any> {
  return analytics.track(ProductEvents.ProductClicked, eventData);
}

export function trackProductListViewed(eventData: TListViewedProps): Promise<any> {
  return analytics.track(ProductEvents.ProductListViewed, eventData);
}

export function trackProductListFiltered(eventData: TListFilteredProps): Promise<any> {
  return analytics.track(ProductEvents.ProductListFiltered, eventData);
}

export function trackPLAListViewed(eventData: TListViewedProps): Promise<any> {
  return analytics.track(ProductEvents.PLAListViewed, eventData);
}

export function trackPLAListFiltered(eventData: TListFilteredProps): Promise<any> {
  return analytics.track(ProductEvents.PLAListFiltered, eventData);
}

let trackProductViewdCallCount = 0;

export function trackProductViewed(
  product: IProduct & Record<string, any>,
  sellerId?: number,
  pdpV2Data?: TPdpV2Data
): Promise<any> {
  /**
   * trackProductViewdCallCount foi criado para corrigir temporariamente
   * o problema de envio duplicado para a segment que gera aumento de custos
   * com a quantidade de requisições. Isso se da pelo componente de Product
   * renderizar 2x em contextos diferentes.
   */
  trackProductViewdCallCount += 1;
  if (trackProductViewdCallCount > 1) return null;

  const cookies = parseCookies();
  const { algIndex } = cookies;

  const productPayload = productPayloadFormatted(product, sellerId);
  const { buyBox, variants } = product;
  const sellerIndex = sellerId ? buyBox.findIndex(item => item.seller.id === sellerId) : 0;

  const { price, installments } = buyBox[Math.max(0, sellerIndex)];
  const isFullfillment = productPayload?.product_tag
    ? productPayload.product_tag.includes('envio_imediato')
    : false;

  const eventPayload: TProductViewed = {
    ...productPayload,
    shipping_method: null,
    sellers_quantity: buyBox.length,
    warranty_months: null,
    fulfillment: isFullfillment,
    full_installment_price: price.total,
    card_discount: installments[0]?.discountPercentage,
    card_price: installments[0]?.total,
    variant_attributes: variants?.options ? Object.keys(variants.options) : [],
    installments,
    product_hidden_tag: [],
    stack: 'atlas',
    index: algIndex,
    product_page_type: pdpV2Data?.trackingValue,
  };

  return analytics.track(ProductEvents.ProductViewed, eventPayload);
}

export function trackProductAdded(
  product: IProduct & Record<string, any>,
  buttonType: string
): Promise<any> {
  const { buyBox } = product;

  const { price, installmentToDisplay } = buyBox[0];
  const { total, discountPercentage } = Array.isArray(installmentToDisplay)
    ? installmentToDisplay[0]
    : installmentToDisplay;

  const productPayload = productPayloadFormatted(product);

  const eventPayload: TProductAdded = {
    ...productPayload,
    ticket_price: price.total,
    original_price: price.productMPrice,
    full_installment_price: price.total,
    card_discount: discountPercentage ?? 0,
    card_price: total,
    button_type: buttonType,
    stack: 'atlas',
  };

  return analytics.track(ProductEvents.ProductAdded, eventPayload);
}

export function trackProductAddedToWishlist(wishlistItem: TProductAddedToWishlist): Promise<any> {
  return analytics.track(ProductEvents.ProductAddedToWishlist, wishlistItem);
}

export function trackWishlistButtonClicked(wishlistItem: TWishlistButtonClicked): Promise<any> {
  return analytics.track(ProductEvents.WishlistButtonClicked, wishlistItem);
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function productPayloadFormatted(
  product: IProduct & Record<string, any>,
  sellerId?: number
): TProductBase {
  const {
    brand,
    buyBox,
    // eslint-disable-next-line camelcase
    cart_id,
    color,
    currency,
    id,
    matrixId,
    ean,
    images,
    flags,
    name,
    objectID,
    quantity,
    rating,
    variants,
    url,
    urls,
  } = product;

  const sellerIndex = sellerId ? buyBox.findIndex(item => item.seller.id === sellerId) : 0;

  const { price, sku, seller, stock } = buyBox[Math.max(0, sellerIndex)];
  const positionVariant = variants?.items
    ? Object.values(variants.items).findIndex((item: any) => item.productId === id)
    : 0;

  const tagFlags: string[] = Object.values(flags).reduce(
    (prevVal: Array<string>, flag: Record<string, any>) => {
      const { name: flagName, value } = flag;

      if (value) {
        switch (flagName) {
          case 'onSale':
            prevVal.push('top_ofertas');
            break;

          case 'fastDelivery':
            prevVal.push('envio_imediato');
            break;

          case 'bestSelling':
            prevVal.push('campeao_de_vendas');
            break;

          default:
            prevVal.push(flagName);
            break;
        }
      }

      return prevVal;
    },
    []
  );

  const cookies = parseCookies();
  const { queryID, algIndex } = cookies;

  return {
    availability: stock > 0,
    average_rating: rating?.rate?.average || null,
    brand: brand || null,
    // eslint-disable-next-line camelcase
    cart_id: cart_id || null,
    category: urls?.category || null,
    coupon: null,
    currency: currency || 'BRL',
    discount_tag: price.productMDiscount,
    ean: String(ean),
    image_url: images.map(elem => elem.lst),
    index: algIndex || null,
    name,
    objectID: String(objectID),
    position: null,
    price: price.inCash,
    product_id: String(id),
    product_matrix: String(matrixId),
    product_tag: tagFlags,
    product_type: seller.id ? '3P' : '1P',
    quantity: quantity || 1,
    queryID: queryID || null,
    reviews_quantity: rating?.rate?.count || null,
    seller: seller.name,
    seller_id: String(seller.id),
    sku: String(sku),
    total_variants: variants?.items ? Object.values(variants.items).length : 1,
    url,
    variant: color,
    variant_position: positionVariant + 1,
  };
}

export function trackCheckInstallments(eventData: TProductCheckInstallments): Promise<any> {
  return analytics.track(ProductEvents.ProductCheckInstallments, eventData);
}

export function trackAddProductToCart(eventData: TProductCartInteraction): void {
  return trackEvent(ProductEvents.AddToCart, eventData);
}

export function trackRemoveProductFromCart(eventData: TProductCartInteraction): void {
  return trackEvent(ProductEvents.ProductRemoveFromCart, eventData);
}

export function trackBuyTogetherChecked(eventData: TBuyTogetherChecked): void {
  return trackEvent(ProductEvents.ProductShowCaseSelection, eventData);
}

export function trackBuyTogetherAddToCart(eventData: TBuyTogetherAddToCart): void {
  return trackEvent(ProductEvents.ProductBoughtTogether, eventData);
}

export function trackBuyTogetherCLickProduct(eventData: TBuyTogetherClickProduct): void {
  return trackEvent(ProductEvents.ProductBoughtTogetherClick, eventData);
}

export function wishListProductTracking(
  title: string,
  indexName: string,
  productPayload: (TProduct | IProduct)[]
) {
  const product = [
    getProductPayload(productPayload[0] as IProduct, undefined, undefined, indexName),
  ];

  const eventData: any = {
    event: title,
    event_date: new Date(),
    product,
  };

  if (title === 'AddWishlistAttempt') {
    sessionStorage.setItem('wishlistAttempt', 'attemptSucess');
  }
  if (title === 'AddToWishlist') {
    sessionStorage.removeItem('wishlistAttempt');
  }

  return window.dataLayer.push(eventData);
}

export function trackProductClickGTM({
  hit,
  currentIndex,
  title,
  position,
  searchOrigin = false,
  isFiltered,
  listShippingFilter = false,
  listId = null,
}: TProductClick) {
  handleAddProductListOrigin({
    list: listId || title,
    productId: hit?.id || hit?.objectID,
    added: false,
    isSponsored: !!hit?.sponsoredData,
  });

  const productFormated = !searchOrigin
    ? getProductPayload(
        hit as unknown as IProduct,
        undefined,
        undefined,
        currentIndex,
        position,
        false,
        listId
      )
    : getProductCLickSearchPayload(hit);

  return trackEvent(ProductEvents.ProductClick, {
    list_id: `${listId || title}`,
    ...(title.includes('Busca') && {
      search_id: sessionStorage.getItem('mm_analytics_searchId'),
    }),
    list_filtered: isFiltered,
    list_shipping_filter: listShippingFilter,
    product: [productFormated],
  });
}

export const trackImageArrows = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ImageArrows,
  });

export const trackImageGalleryArrows = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ImageGalleryArrows,
  });

export const trackImageSelect = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ImageSelect,
  });

export const trackThumbSelect = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ThumbSelect,
  });

export const trackVariantSelectClick = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.VariantSelect,
  });

export const trackItemIncreaseClick = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ItemIncrease,
  });

export const trackItemDecreaseClick = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ItemDecrease,
  });

export const trackManualShippingCalc = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: eventData.interaction_type || ProductInteraction.ManualShippingCalc,
  });

export const trackAutomaticShippingCalc = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.AutomaticShippingCalc,
  });

export const trackPartnerShippingCalc = (eventData: {
  interaction_value?: Nullable<string | number>;
}): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_location: 'Parceiros',
    interaction_type: ProductInteraction.PartnerShippingCalc,
  });

export const trackChangeWarranty = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ChangeWarranty,
  });

export const trackCheckboxWarrantyClick = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.CheckboxWarranty,
  });

export const trackProductExtendedWarranty = (eventData: TProductExtendedWarranty): void =>
  trackEvent(ProductEvents.ProductExtendedWarranty, {
    ...eventData,
  });
export const trackProductDescriptionTab = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ProductDescriptionTab,
  });

export const trackProductInformationTab = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ProductInformationTab,
  });

export const trackProductRatingsTab = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ProductRatingsTab,
  });

export const trackProductQuestionsTab = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ProductQuestionsTab,
  });

export const trackExtendedWarrantyTab = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ExtendedWarrantyTab,
  });

export const trackHyperlinkRating = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.HyperlinkRatings,
  });

export const trackHyperlinkProductInformation = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.HyperlinkProductInformation,
  });

export const trackModularDetailsClick = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ProductModularDetail,
  });

export const trackModularQuantity = (
  eventData: TInteractionEvent,
  step: number,
  stepCurrent: number
): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type:
      step > stepCurrent ? ProductInteraction.IncreaseModule : ProductInteraction.DecreaseModule,
  });

export const trackViewModuleItemDetail = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ViewModuleItemDetail,
  });
export const trackSetItemDetail = async (eventData: TInteractionEvent): Promise<any> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.SetItemDetail,
  });

export const trackViewSetItemDetail = async (eventData: TInteractionEvent): Promise<any> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ViewSetItemDetail,
  });

export const trackProductInteractionWarranty = async (eventData: TInteractionEvent): Promise<any> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
  });

export const trackProductInteractionSendOutOfStockAlert = async (
  eventData: TInteractionEvent
): Promise<any> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.SendOutOfStockAlert,
  });

export const trackProductInteractionUnavailableCTA = async (
  eventData: TInteractionEvent
): Promise<any> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.UnavailableCTA,
  });

export const trackProductInteractionServiceZipcode = async (
  eventData: TInteractionEvent
): Promise<any> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ProductServiceInsertZipcode,
  });

export const trackProductShowAllCharacteristics = async (
  eventData: TInteractionEvent
): Promise<void> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ShowAllCharacteristics,
  });

function getAttributeConfirmedFlag(product: IProduct): boolean {
  return !!product?.productAttributesConfig?.attributesConfigList?.length;
}

function getInfoboxComponentText(product: IProduct): string | undefined {
  const productPageActionBox = product?.productPageActionBox;
  const infoBox = productPageActionBox?.productActionBox?.infoBox;

  if (!productPageActionBox?.activeActionBox || !infoBox) {
    return undefined;
  }

  return `${infoBox.content}${infoBox.link}` || undefined;
}

export function trackProductDetail(
  trackingPayload: IProduct,
  shipping?: IBuyBox[],
  pageType?: string,
  sellerId?: number
): void {
  if (!['PDP', 'PLA'].includes(pageType)) {
    return null;
  }

  const product = getProductPayload(
    trackingPayload,
    shipping,
    sellerId,
    undefined,
    undefined,
    false
  );

  const featureFlagCtaComponent = getInfoboxComponentText(trackingPayload);
  const featureFlagAttributeConfirmedComponent = getAttributeConfirmedFlag(trackingPayload);

  setTimeout(() => {
    sessionStorage.removeItem('trackHasSend');
  }, 5000);

  const getProductPageType = () => {
    if (pageType === PAGE_TYPE.PLA) {
      return PAGE_TYPE.PLA;
    }

    if (trackingPayload.buyBox.length > 1) {
      return 'PDP - 3P elegível';
    }

    return 'PDP - Primeira Leva';
  };

  return trackEvent(ProductEvents.ProductDetail, {
    product,
    product_page_type: getProductPageType(),
    feature_flag_cta_component: featureFlagCtaComponent,
    feature_flag_attribute_confirmed_component: featureFlagAttributeConfirmedComponent,
  });
}

export const trackPaymentOptions = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.PaymentOption,
  });

export const trackHowToBuy = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.HowToBuy,
  });

export const trackWarrantyClick = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ClickWarranty,
  });

export const trackWhatsappNotification = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.WhatsappNotification,
  });

export const trackPaymentOptionDetail = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.PaymentOptionDetail,
  });

export const trackRating = (eventData: TInteractionEvent, type: string): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: type,
  });

export const trackProductInteractionFractionalCalculator = async (
  eventData: TInteractionEvent
): Promise<any> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.FractionalCalculator,
  });

export const trackProductInteractionHowToCalculate = async (
  eventData: TInteractionEvent
): Promise<any> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.HowToCalculate,
  });

export const trackProductInteractionShare = async (eventData: TInteractionEvent): Promise<any> => {
  if (!eventData?.interaction_location) {
    return null;
  }

  return trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.Share,
  });
};

export const trackProductSendQuestion = async (eventData: TInteractionEvent): Promise<void> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.SendQuestion,
  });

export const trackProductShowAllQuestions = async (eventData: TInteractionEvent): Promise<void> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ShowAllQuestions,
  });

export const trackProductShowAllRatings = async (eventData: TInteractionEvent): Promise<void> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ShowAllRatings,
  });

export const trackProductShowAllDescription = async (eventData: TInteractionEvent): Promise<void> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ShowAllDescription,
  });

export const trackProductInteractionCallSpecialist = async (
  eventData: TInteractionEvent
): Promise<void> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.CallSpecialist,
  });

export const trackProductInteractionCTA = async (eventData: TInteractionEvent): Promise<void> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.CtaComponent,
  });

export const TrackProductInteractionScrollToTop = async (
  eventData: TInteractionEvent
): Promise<void> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.ScrollToTop,
  });

export const trackProductInteractionSeeAllSellerProducts = async (
  eventData: TInteractionEvent
): Promise<void> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.SeeAllSellerProducts,
  });

export const trackProductInteractionSeeMoreSellerProducts = async (
  eventData: TInteractionEvent
): Promise<void> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.SeeMoreSellerProducts,
  });

export const trackProductInteractionCollapse = async (
  eventData: TInteractionEvent
): Promise<void> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.Collapse,
  });

export const trackProductMontagem = async (eventData: TInteractionEvent): Promise<void> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.AddMontagem,
  });

export const trackProductImpermeabilizacao = async (eventData: TInteractionEvent): Promise<void> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.AddImpermeabilizacao,
  });

export const trackProductPlayVideo = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.PlayVideo,
  });

export const trackProductGalleryPlayVideo = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.GalleryPlayVideo,
  });

export const trackInteractionGuideShop = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: eventData.interaction_type,
  });

export const trackInteractionOpenAssembledPhotos = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.AssemblyGalleryTab,
  });

export const trackInteractionSelectAssembledPhotos = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.AssemblyGallerySelect,
  });

export const trackProductInteractionCouponCopy = async (
  eventData: TInteractionEvent
): Promise<void> =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.CouponCopy,
  });

export const trackPendingReviewVisualization = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.PendingReviewVisualization,
  });

export const trackPendingReviewClose = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.PendingReviewCloseComponent,
  });

export const trackPendingReviewSeeAll = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.PendingReviewSeeAll,
  });

export const trackPendingReviewProductRating = (eventData: TInteractionEvent): void =>
  trackEvent(ProductEvents.ProductInteraction, {
    ...eventData,
    interaction_type: ProductInteraction.PendingReviewProductRating,
  });
